import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Share2, Eye, EyeOff, Users, Copy, Check, ExternalLink,
  Code, FileText, Linkedin, Mail, RefreshCw, Shield
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * E6: Share Tab - Badge and credential sharing page
 * 
 * Features:
 * - Live badge preview with style/theme selection
 * - Four embed tabs: HTML, Markdown, LinkedIn, Email
 * - Visibility toggle with inline consent for open
 * - Copy-to-clipboard functionality
 */
export default function Share() {
  const { user } = useAuth();
  
  // Badge configuration
  const [badgeStyle, setBadgeStyle] = useState('card');
  const [badgeTheme, setBadgeTheme] = useState('dark');
  const [vettId, setVettId] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  
  // Visibility state
  const [visibility, setVisibility] = useState('selective');
  const [loadingVisibility, setLoadingVisibility] = useState(true);
  
  // Consent dialog
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [pendingVisibility, setPendingVisibility] = useState(null);
  
  // Copy state
  const [copiedTab, setCopiedTab] = useState(null);
  
  // LinkedIn URL
  const [linkedInUrl, setLinkedInUrl] = useState(null);
  const [loadingLinkedIn, setLoadingLinkedIn] = useState(false);

  // Fetch profile data and visibility
  const fetchProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch profile to get vett_id
      const profileRes = await fetch(`${API_URL}/api/employee/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        setVettId(profileData.vett_id);
        setProfileLoaded(true);
      }
      
      // Fetch visibility
      const visRes = await fetch(`${API_URL}/api/employee/visibility`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (visRes.ok) {
        const visData = await visRes.json();
        setVisibility(visData.visibility || 'selective');
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoadingVisibility(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Fetch LinkedIn URL when vettId available
  useEffect(() => {
    if (vettId && visibility !== 'private') {
      fetchLinkedInUrl();
    }
  }, [vettId, visibility]);

  const fetchLinkedInUrl = async () => {
    if (!vettId) return;
    
    setLoadingLinkedIn(true);
    try {
      const response = await fetch(`${API_URL}/api/badge/${vettId}/linkedin`);
      if (response.ok) {
        const data = await response.json();
        setLinkedInUrl(data.linkedin_add_url);
      }
    } catch (error) {
      console.error('Error fetching LinkedIn URL:', error);
    } finally {
      setLoadingLinkedIn(false);
    }
  };

  // Handle visibility change with consent flow
  const handleVisibilityChange = async (newVisibility) => {
    // Changing TO open requires consent
    if (newVisibility === 'open' && visibility !== 'open') {
      setPendingVisibility('open');
      setShowConsentDialog(true);
      return;
    }
    
    // Direct change for other transitions
    await updateVisibility(newVisibility);
  };

  const updateVisibility = async (newVisibility, consentGiven = false) => {
    try {
      const token = localStorage.getItem('token');
      
      const body = { visibility: newVisibility };
      if (consentGiven) {
        body.consent_given = true;
      }
      
      const response = await fetch(`${API_URL}/api/employee/visibility`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        setVisibility(newVisibility);
        toast.success(`Profile visibility set to ${newVisibility}`);
        
        // Refresh LinkedIn URL if moving away from private
        if (visibility === 'private' && newVisibility !== 'private') {
          fetchLinkedInUrl();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update visibility');
      }
    } catch (error) {
      console.error('Error updating visibility:', error);
      toast.error('Failed to update visibility setting');
    }
  };

  const handleConsentConfirm = async () => {
    setShowConsentDialog(false);
    await updateVisibility(pendingVisibility, true);
    setPendingVisibility(null);
  };

  const handleConsentCancel = () => {
    setShowConsentDialog(false);
    setPendingVisibility(null);
  };

  // Generate embed codes
  const generateEmbedCodes = () => {
    if (!vettId) return { html: '', markdown: '', email: '' };
    
    const publicUrl = `${API_URL}/id/${vettId}`;
    const svgUrl = `${API_URL}/api/badge/${vettId}.svg?style=${badgeStyle}&theme=${badgeTheme}`;
    const pngUrl = `${API_URL}/api/badge/${vettId}.png?style=${badgeStyle}&theme=${badgeTheme}`;
    const embedUrl = `${API_URL}/api/badge/${vettId}/embed?style=${badgeStyle}&theme=${badgeTheme}`;
    
    const html = `<!-- VETT Badge -->
<a href="${publicUrl}" target="_blank" rel="noopener noreferrer">
  <img src="${pngUrl}" alt="VETT Verified Badge" style="border: none;" />
</a>`;
    
    const htmlIframe = `<!-- VETT Badge (iframe) -->
<iframe src="${embedUrl}" 
  width="${getBadgeDimensions().width}" 
  height="${getBadgeDimensions().height + 20}" 
  frameborder="0" 
  scrolling="no"
  title="VETT Verified Badge">
</iframe>`;
    
    const markdown = `[![VETT Verified Badge](${pngUrl})](${publicUrl})`;
    
    const email = `<table cellpadding="0" cellspacing="0" border="0">
  <tr>
    <td>
      <a href="${publicUrl}" target="_blank">
        <img src="${pngUrl}" alt="VETT Verified" style="border: none; display: block;" />
      </a>
    </td>
  </tr>
</table>`;
    
    return { html, htmlIframe, markdown, email, svgUrl, pngUrl, embedUrl, publicUrl };
  };

  const getBadgeDimensions = () => {
    const dimensions = {
      card: { width: 320, height: 180 },
      compact: { width: 240, height: 80 },
      button: { width: 180, height: 36 },
      seal: { width: 120, height: 120 },
      inline: { width: 100, height: 20 },
    };
    return dimensions[badgeStyle] || dimensions.card;
  };

  const copyToClipboard = async (text, tabName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedTab(tabName);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedTab(null), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const codes = generateEmbedCodes();
  const badgeDimensions = getBadgeDimensions();

  // Visibility icons and labels
  const visibilityConfig = {
    open: { icon: Eye, label: 'Open', color: 'text-emerald-400', description: 'Visible to anyone' },
    selective: { icon: Users, label: 'Selective', color: 'text-blue-400', description: 'Visible to approved employers' },
    private: { icon: EyeOff, label: 'Private', color: 'text-gray-400', description: 'Hidden from everyone' },
  };

  const styles = [
    { id: 'card', label: 'Card', dimensions: '320×180' },
    { id: 'compact', label: 'Compact', dimensions: '240×80' },
    { id: 'button', label: 'Button', dimensions: '180×36' },
    { id: 'seal', label: 'Seal', dimensions: '120×120' },
    { id: 'inline', label: 'Inline', dimensions: '100×20' },
  ];

  const themes = [
    { id: 'dark', label: 'Dark' },
    { id: 'light', label: 'Light' },
    { id: 'auto', label: 'Auto' },
  ];

  if (!profileLoaded || loadingVisibility) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="share-loading">
        <RefreshCw className="w-8 h-8 text-[#00C9A7] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="share-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Share2 className="w-8 h-8 text-[#00C9A7]" />
        <div>
          <h1 className="text-2xl font-bold text-[#0A0F1E]">Share Your VETT Badge</h1>
          <p className="text-[#5B6472]">Embed your verified career credentials anywhere</p>
        </div>
      </div>

      {/* Visibility Control Card */}
      <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6" data-testid="visibility-control">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#00C9A7]" />
            <h2 className="text-lg font-semibold text-[#0A0F1E]">Profile Visibility</h2>
          </div>
          <Badge 
            variant="outline" 
            className={`${visibilityConfig[visibility].color} border-current`}
            data-testid="current-visibility-badge"
          >
            {visibilityConfig[visibility].label}
          </Badge>
        </div>
        
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(visibilityConfig).map(([key, config]) => {
            const Icon = config.icon;
            const isActive = visibility === key;
            
            return (
              <button
                key={key}
                onClick={() => handleVisibilityChange(key)}
                className={`p-4 rounded-lg border transition-all ${
                  isActive
                    ? 'border-[#00C9A7] bg-[#00C9A7]/10'
                    : 'border-[#E8EDF5] hover:border-[#3A3A5A] bg-[#FFFFFF]'
                }`}
                data-testid={`visibility-${key}-btn`}
              >
                <Icon className={`w-5 h-5 mx-auto mb-2 ${isActive ? 'text-[#00C9A7]' : 'text-[#5B6472]'}`} />
                <p className={`text-sm font-medium ${isActive ? 'text-[#0A0F1E]' : 'text-[#5B6472]'}`}>
                  {config.label}
                </p>
                <p className="text-xs text-[#6666AA] mt-1">{config.description}</p>
              </button>
            );
          })}
        </div>
        
        {visibility === 'private' && (
          <div className="mt-4 p-3 bg-[#1B5AFF]/10 border border-[#1B5AFF]/30 rounded-lg">
            <p className="text-[#1B5AFF] text-sm">
              Your badge is currently hidden. Change visibility to share your credentials.
            </p>
          </div>
        )}
      </Card>

      {/* Main Content - Only show if not private */}
      {visibility !== 'private' && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Live Preview */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6" data-testid="badge-preview-card">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Live Preview</h2>
            
            {/* Style Selection */}
            <div className="mb-4">
              <label className="text-sm text-[#5B6472] mb-2 block">Style</label>
              <div className="flex flex-wrap gap-2">
                {styles.map((style) => (
                  <button
                    key={style.id}
                    onClick={() => setBadgeStyle(style.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                      badgeStyle === style.id
                        ? 'bg-[#00C9A7] text-black font-medium'
                        : 'bg-[#F5F8FF] text-[#5B6472] hover:bg-[#3A3A5A]'
                    }`}
                    data-testid={`style-${style.id}-btn`}
                  >
                    {style.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Theme Selection */}
            <div className="mb-6">
              <label className="text-sm text-[#5B6472] mb-2 block">Theme</label>
              <div className="flex gap-2">
                {themes.map((theme) => (
                  <button
                    key={theme.id}
                    onClick={() => setBadgeTheme(theme.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-all ${
                      badgeTheme === theme.id
                        ? 'bg-[#1B5AFF] text-[#0A0F1E] font-medium'
                        : 'bg-[#F5F8FF] text-[#5B6472] hover:bg-[#3A3A5A]'
                    }`}
                    data-testid={`theme-${theme.id}-btn`}
                  >
                    {theme.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Badge Preview */}
            <div 
              className="flex items-center justify-center p-8 bg-[#FFFFFF] rounded-lg border border-[#E8EDF5] min-h-[200px]"
              data-testid="badge-preview"
            >
              {vettId ? (
                <iframe
                  src={codes.embedUrl}
                  width={badgeDimensions.width + 20}
                  height={badgeDimensions.height + 20}
                  frameBorder="0"
                  scrolling="no"
                  title="VETT Badge Preview"
                  data-testid="badge-iframe"
                />
              ) : (
                <p className="text-[#5B6472]">Loading preview...</p>
              )}
            </div>
            
            {/* Dimensions info */}
            <p className="text-xs text-[#6666AA] mt-3 text-center">
              {styles.find(s => s.id === badgeStyle)?.dimensions} pixels
            </p>
            
            {/* Public page link */}
            <div className="mt-4 pt-4 border-t border-[#E8EDF5]">
              <a
                href={codes.publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 text-[#00C9A7] hover:text-[#00E5BF] transition-colors"
                data-testid="public-page-link"
              >
                <ExternalLink className="w-4 h-4" />
                <span>View Public Profile Page</span>
              </a>
            </div>
          </Card>

          {/* Embed Codes */}
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6" data-testid="embed-codes-card">
            <h2 className="text-lg font-semibold text-[#0A0F1E] mb-4">Embed Codes</h2>
            
            <Tabs defaultValue="html" className="w-full">
              <TabsList className="grid w-full grid-cols-4 bg-[#FFFFFF] mb-4">
                <TabsTrigger value="html" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] rounded-lg" data-testid="tab-html">
                  <Code className="w-4 h-4 mr-1" />
                  HTML
                </TabsTrigger>
                <TabsTrigger value="markdown" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] rounded-lg" data-testid="tab-markdown">
                  <FileText className="w-4 h-4 mr-1" />
                  MD
                </TabsTrigger>
                <TabsTrigger value="linkedin" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] rounded-lg" data-testid="tab-linkedin">
                  <Linkedin className="w-4 h-4 mr-1" />
                  LinkedIn
                </TabsTrigger>
                <TabsTrigger value="email" className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] rounded-lg" data-testid="tab-email">
                  <Mail className="w-4 h-4 mr-1" />
                  Email
                </TabsTrigger>
              </TabsList>

              {/* HTML Tab */}
              <TabsContent value="html" className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-[#5B6472]">Image embed</label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(codes.html, 'html')}
                      className="text-[#00C9A7] hover:text-[#00E5BF] h-8"
                      data-testid="copy-html-btn"
                    >
                      {copiedTab === 'html' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <pre className="bg-[#FFFFFF] p-3 rounded-lg text-xs text-[#5B6472] overflow-x-auto whitespace-pre-wrap font-mono" data-testid="html-code">
                    {codes.html}
                  </pre>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-[#5B6472]">iFrame embed</label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(codes.htmlIframe, 'iframe')}
                      className="text-[#00C9A7] hover:text-[#00E5BF] h-8"
                      data-testid="copy-iframe-btn"
                    >
                      {copiedTab === 'iframe' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <pre className="bg-[#FFFFFF] p-3 rounded-lg text-xs text-[#5B6472] overflow-x-auto whitespace-pre-wrap font-mono" data-testid="iframe-code">
                    {codes.htmlIframe}
                  </pre>
                </div>
              </TabsContent>

              {/* Markdown Tab */}
              <TabsContent value="markdown" className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-[#5B6472]">Markdown badge</label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(codes.markdown, 'markdown')}
                    className="text-[#00C9A7] hover:text-[#00E5BF] h-8"
                    data-testid="copy-markdown-btn"
                  >
                    {copiedTab === 'markdown' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <pre className="bg-[#FFFFFF] p-3 rounded-lg text-xs text-[#5B6472] overflow-x-auto whitespace-pre-wrap font-mono" data-testid="markdown-code">
                  {codes.markdown}
                </pre>
                <p className="text-xs text-[#6666AA]">
                  Works with GitHub, GitLab, and most Markdown renderers.
                </p>
              </TabsContent>

              {/* LinkedIn Tab */}
              <TabsContent value="linkedin" className="space-y-4">
                <div className="p-4 bg-[#FFFFFF] rounded-lg">
                  <p className="text-sm text-[#5B6472] mb-4">
                    Add your VETT certification to your LinkedIn profile with one click.
                  </p>
                  
                  {loadingLinkedIn ? (
                    <div className="flex items-center justify-center py-4">
                      <RefreshCw className="w-5 h-5 text-[#00C9A7] animate-spin" />
                    </div>
                  ) : linkedInUrl ? (
                    <a
                      href={linkedInUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[#0A66C2] hover:bg-[#004182] text-[#0A0F1E] rounded-lg transition-colors"
                      data-testid="linkedin-add-btn"
                    >
                      <Linkedin className="w-5 h-5" />
                      Add to LinkedIn Profile
                    </a>
                  ) : (
                    <p className="text-[#1B5AFF] text-sm">Unable to generate LinkedIn URL</p>
                  )}
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-[#5B6472]">LinkedIn URL</label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => linkedInUrl && copyToClipboard(linkedInUrl, 'linkedin')}
                      className="text-[#00C9A7] hover:text-[#00E5BF] h-8"
                      disabled={!linkedInUrl}
                      data-testid="copy-linkedin-btn"
                    >
                      {copiedTab === 'linkedin' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <pre className="bg-[#FFFFFF] p-3 rounded-lg text-xs text-[#5B6472] overflow-x-auto whitespace-pre-wrap font-mono" data-testid="linkedin-url">
                    {linkedInUrl || 'Loading...'}
                  </pre>
                </div>
              </TabsContent>

              {/* Email Tab */}
              <TabsContent value="email" className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-[#5B6472]">Email signature HTML</label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(codes.email, 'email')}
                    className="text-[#00C9A7] hover:text-[#00E5BF] h-8"
                    data-testid="copy-email-btn"
                  >
                    {copiedTab === 'email' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <pre className="bg-[#FFFFFF] p-3 rounded-lg text-xs text-[#5B6472] overflow-x-auto whitespace-pre-wrap font-mono" data-testid="email-code">
                  {codes.email}
                </pre>
                <p className="text-xs text-[#6666AA]">
                  Copy this HTML into your email client&apos;s signature settings.
                </p>
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      )}

      {/* Open Visibility Consent Dialog */}
      <AlertDialog open={showConsentDialog} onOpenChange={setShowConsentDialog}>
        <AlertDialogContent className="bg-[#FFFFFF] border border-[#E8EDF5]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0A0F1E]">Enable Open Visibility?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#5B6472]">
              By enabling <span className="text-[#00C9A7] font-medium">open visibility</span>, your 
              VETT badge and verified career credentials will be publicly viewable by anyone with the link.
              <br /><br />
              This includes your:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Name and tier level</li>
                <li>Career score</li>
                <li>Number of verified records</li>
                <li>VETT ID</li>
              </ul>
              <br />
              Sensitive details like manager assessments, rehire eligibility, and internal notes are 
              <span className="text-[#00C9A7] font-medium"> never shared publicly</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={handleConsentCancel}
              className="bg-[#F5F8FF] text-[#0A0F1E] border-none hover:bg-[#3A3A5A]"
              data-testid="consent-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConsentConfirm}
              className="bg-[#00C9A7] text-black hover:bg-[#00E5BF]"
              data-testid="consent-confirm-btn"
            >
              I Consent, Enable Open Visibility
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
